/**
 * The resume pipeline's status vocabulary, and the rules for reading it.
 *
 * Mirrors ResumeStatus in app/models.py. A resume row now exists from the moment its PDF is
 * uploaded rather than from the moment parsing succeeds, so the table has to render rows that
 * have no score yet — and say something truthful about why.
 *
 * Pure functions, no React: this is the part worth testing, and the part that must not disagree
 * with the server.
 */

export const RESUME_STATUS = {
  UPLOADED: "uploaded",
  PARSING: "parsing",
  PARSED: "parsed",
  SCORING: "scoring",
  SCORED: "scored",
  FAILED: "failed",
};

/** Statuses from which nothing further happens on its own. */
const TERMINAL = new Set([RESUME_STATUS.SCORED, RESUME_STATUS.FAILED]);

export const isTerminal = (status) => TERMINAL.has(status);

/**
 * Is this row still being worked on *right now*?
 *
 * Takes the mapped row (see candidateRow.toRow), not the raw payload.
 *
 * Stale rows are NOT in progress, even though their status is non-terminal. This distinction is
 * what stops the console polling forever. Two kinds of row look mid-pipeline but are not:
 *
 *   - a row whose worker died (status stopped moving; the server flags it stale), and
 *   - the orphans the migration backfilled to `parsed` — resumes that parsed but were never
 *     scored back when scoring depended on a browser staying open. Nothing is processing them
 *     and nothing will until someone hits Retry.
 *
 * Treating either as "in progress" would mean re-fetching the rankings every 3 seconds, forever,
 * for a row that will never change on its own.
 */
export const isInProgress = (row) => !isTerminal(row?.status) && !row?.isStale;

/** Poll only while something can actually change. */
export const hasWorkInFlight = (rows = []) => rows.some(isInProgress);

/**
 * What to show in the status column. `null` means the row is a normal scored candidate and the
 * column should stay out of the way.
 */
export const statusChip = (row) => {
  const status = row?.status;
  const stale = row?.isStale;

  if (status === RESUME_STATUS.FAILED) {
    return { label: "Failed", tone: "danger", retryable: true, detail: row?.error };
  }

  // Stale beats the nominal status: the row claims to be parsing, but nothing is parsing it.
  // Reporting "Parsing…" here is the exact lie this whole change exists to stop telling.
  if (stale) {
    return {
      label: "Stuck",
      tone: "danger",
      retryable: true,
      detail: "Processing stopped unexpectedly. Retry to try again.",
    };
  }

  switch (status) {
    case RESUME_STATUS.UPLOADED:
      return { label: "Queued", tone: "muted", retryable: false };
    case RESUME_STATUS.PARSING:
      return { label: "Parsing…", tone: "progress", retryable: false };
    case RESUME_STATUS.PARSED:
      return { label: "Waiting to score", tone: "progress", retryable: false };
    case RESUME_STATUS.SCORING:
      return { label: "Scoring…", tone: "progress", retryable: false };
    case RESUME_STATUS.SCORED:
    default:
      return null;
  }
};

/** A row with no score yet has nothing to put in a score column. Say so, don't render a 0. */
export const hasScore = (row) => row?.status === RESUME_STATUS.SCORED;
